import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Card, 
  Table, 
  Tag, 
  Space, 
  Typography, 
  Alert,
  Button,
  Input,
  Select,
  Row,
  Col,
  Statistic,
  Progress,
  Drawer,
  Radio,
  Popover,
  Switch,
  theme,
} from 'antd';
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined,
  InfoCircleOutlined,
  TableOutlined,
  QuestionCircleOutlined,
  SyncOutlined,
  FolderOutlined,
  SafetyOutlined,
  CloudUploadOutlined,
  TagOutlined,
  CalendarOutlined,
  CopyOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { ResetIcon, RefreshIcon } from '../../../common/ActionIcons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getProcessingJobsByUploadBatch } from '../../../../actions/processingJobActions';
import ReactECharts from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { ThemeContext } from '../../../../index';

echarts.use([TooltipComponent, PieChart, CanvasRenderer]);

const { Text, Title } = Typography;

/** Right drawer sidebar — section headings above panels */
const drawerSidebarSectionTitleProps = {
  level: 5,
  style: {
    marginTop: 0,
    marginBottom: 10,
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.35,
  },
};

/** Auto-refresh while upload batch is still in flight (not terminal). */
const UPLOAD_BATCH_AUTO_REFRESH_STATUSES = new Set(['pending', 'processing', 'validated']);

function isUploadBatchAutoRefreshActive(status) {
  if (status == null || status === '') return false;
  return UPLOAD_BATCH_AUTO_REFRESH_STATUSES.has(String(status).trim().toLowerCase());
}

/** API may return jsonb as object or serialized string */
function parseProcessingJobJson(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

/** Backend traffic-light style status (e.g. Amber, Green) → dot color */
function dataQualityStatusColor(status) {
  if (status == null || status === '') return '#bfbfbf';
  const s = String(status).trim().toLowerCase();
  if (s.includes('green')) return '#52c41a';
  if (s.includes('amber') || s.includes('yellow') || s.includes('orange')) return '#faad14';
  if (s.includes('red')) return '#ff4d4f';
  if (s.includes('blue')) return '#1890ff';
  return '#8c8c8c';
}

function formatUploadBatchStatNumber(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n : '—';
}

/** Ingest outcome drawer colours (chart + stat rows). */
const INGEST_RECEIVED_COLOR = '#52c41a';
const INGEST_FAILED_COLOR = '#ff4d4f';
const INGEST_SKIPPED_COLOR = '#8c8c8c';

function computeIngestOutcomes(stats) {
  const processed = Number(stats.processedRecords) || 0;
  const failed = Number(stats.failedRecords) || 0;
  const skipped = Number(stats.skippedRecords) || 0;
  const received = Math.max(0, processed - failed - skipped);
  return { received, failed, skipped, processed };
}

function IngestOutcomeLegendRow({ color, label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 20,
        padding: '2px 0',
        lineHeight: 1.15,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          flexShrink: 0,
        }}
      />
      <Text type="secondary" style={{ fontSize: 12, flex: 1, lineHeight: 1.15 }}>
        {label}
      </Text>
      <Text
        style={{
          fontSize: 13,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          color,
          lineHeight: 1,
        }}
      >
        {formatUploadBatchStatNumber(value)}
      </Text>
    </div>
  );
}

/** Compact ingest outcomes — donut + legend (section title is outside). */
function UploadBatchSidebarLead({ stats, token, t }) {
  const { themeConfig } = useContext(ThemeContext);
  const isDark = themeConfig?.algorithm === theme.darkAlgorithm;
  const chartRef = useRef(null);
  const chartWrapRef = useRef(null);
  const border = token.colorBorderSecondary;
  const panelBg = token.colorFillQuaternary;

  const totalN = Number(stats?.totalRecords);
  const { received, failed, skipped, processed } = stats
    ? computeIngestOutcomes(stats)
    : { received: 0, failed: 0, skipped: 0, processed: 0 };
  const hasTotal = Number.isFinite(totalN) && totalN > 0;
  const outcomeSum = received + failed + skipped;
  const showSkipped = stats?.skippedRecords != null;

  const resizeChart = useCallback(() => {
    chartRef.current?.getEchartsInstance?.()?.resize();
  }, []);

  useEffect(() => {
    if (!stats) return undefined;
    resizeChart();
    const t1 = setTimeout(resizeChart, 100);
    window.addEventListener('resize', resizeChart);
    return () => {
      clearTimeout(t1);
      window.removeEventListener('resize', resizeChart);
    };
  }, [resizeChart, stats]);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => resizeChart());
    ro.observe(el);
    return () => ro.disconnect();
  }, [resizeChart]);

  const outcomePieOption = useMemo(() => {
    if (!stats) return null;
    const slices = [
      { value: received, name: t('Received'), itemStyle: { color: INGEST_RECEIVED_COLOR } },
      { value: failed, name: t('Failed'), itemStyle: { color: INGEST_FAILED_COLOR } },
      { value: skipped, name: t('Skipped'), itemStyle: { color: INGEST_SKIPPED_COLOR } },
    ].filter((d) => d.value > 0);
    const data =
      slices.length > 0
        ? slices
        : [{ value: 1, name: t('No rows'), itemStyle: { color: token.colorFill } }];

    return {
      animationDuration: 280,
      tooltip: {
        trigger: 'item',
        borderWidth: 0,
        padding: [6, 10],
        formatter: (p) => {
          const pctLabel = p.percent != null ? Number(p.percent).toFixed(1) : '';
          return (
            `<strong>${p.name}</strong><br/>` +
            `${p.value}${pctLabel !== '' ? ` · ${pctLabel}%` : ''}`
          );
        },
      },
      series: [
        {
          type: 'pie',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          radius: ['62%', '88%'],
          center: ['50%', '50%'],
          padAngle: outcomeSum > 1 ? 0.04 : 0,
          itemStyle: {
            borderColor: isDark ? '#1f1f1f' : '#fff',
            borderWidth: 1,
          },
          label: { show: false },
          labelLine: { show: false },
          data,
        },
      ],
    };
  }, [stats, received, failed, skipped, outcomeSum, isDark, t, token.colorFill]);

  if (!stats) return null;

  const fileProgressLine = hasTotal
    ? `${formatUploadBatchStatNumber(processed)} ${t('of')} ${formatUploadBatchStatNumber(totalN)} ${t('records processed')}`
    : null;

  return (
    <div
      style={{
        marginBottom: 12,
        padding: 10,
        border: `1px solid ${border}`,
        borderRadius: token.borderRadius,
        background: token.colorBgContainer,
      }}
    >
      <div
        ref={chartWrapRef}
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          marginBottom: 4,
        }}
      >
        <div
          style={{
            width: 112,
            height: 112,
            position: 'relative',
            overflow: 'visible',
            borderRadius: token.borderRadiusSM,
            background: panelBg,
            border: `1px solid ${border}`,
          }}
        >
          <ReactECharts
            ref={chartRef}
            echarts={echarts}
            option={outcomePieOption}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: 124,
              height: 124,
              marginLeft: -62,
              marginTop: -62,
            }}
            notMerge
            lazyUpdate
          />
        </div>
      </div>

      <div
        style={{
          marginTop: 10,
          padding: '8px 12px',
          borderRadius: token.borderRadiusSM,
          background: panelBg,
          border: `1px solid ${border}`,
        }}
      >
        <IngestOutcomeLegendRow
          color={INGEST_RECEIVED_COLOR}
          label={t('Received')}
          value={received}
        />
        <IngestOutcomeLegendRow
          color={INGEST_FAILED_COLOR}
          label={t('Failed')}
          value={failed}
        />
        {showSkipped && (
          <IngestOutcomeLegendRow
            color={INGEST_SKIPPED_COLOR}
            label={t('Skipped')}
            value={skipped}
          />
        )}
      </div>

      {fileProgressLine && (
        <Text
          type="secondary"
          style={{
            display: 'block',
            marginTop: 8,
            fontSize: 10,
            lineHeight: 1.4,
            textAlign: 'center',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fileProgressLine}
        </Text>
      )}
    </div>
  );
}

const ProcessingJobs = ({ 
  uploadBatchId, 
  title = "Processing Jobs",
  /** Called after processing jobs refresh — parent can PATCH one upload batch row without reloading the list. */
  onRefreshUploadBatch,
  filePath,
  fileHash,
  uploadMethod,
  customerPrefix,
  createdAt,
  /** Upload batch row from API: totalRecords, processedRecords, failedRecords, validRecords; drawer adds skippedRecords from job statistics */
  uploadBatchFileStats,
  /** Upload batch lifecycle status (Pending, Processing, Completed, Failed, Error, …) */
  uploadBatchStatus,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const dispatch = useDispatch();

  // Local state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [uploadBatchIdCopied, setUploadBatchIdCopied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [searchProp, setSearchProp] = useState('jobName');
  
  // Progress tracking state
  const [previousStats, setPreviousStats] = useState(null);
  const [progressSpeed, setProgressSpeed] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Preserve current statistics during loading
  const [preservedStats, setPreservedStats] = useState(null);
  const [preservedTotalRecords, setPreservedTotalRecords] = useState(0);

  // Redux state - get the specific upload batch data
  const processingJobByUploadBatch = useSelector(state => state.processingJobByUploadBatch);
  const batchData = processingJobByUploadBatch.processingJobsByBatch?.[uploadBatchId] || {};
  const { 
    loading: loadingByUploadBatch, 
    error: errorByUploadBatch, 
    processingJobs = [],
    totalCount = 0,
    totalUploadBatchRecords = 0,
    statistics = null
  } = batchData;

  // Load processing jobs when component mounts or uploadBatchId changes
  useEffect(() => {
    if (uploadBatchId) {
      const params = {
        page: currentPage,
        limit: pageSize,
        ...(searchText && searchProp && { filter: searchProp, query: searchText })
      };
      console.log('Sending params to backend:', params);
      dispatch(getProcessingJobsByUploadBatch(uploadBatchId, params));
    } else {
      // Reset state if no uploadBatchId provided
      dispatch({ type: 'PROCESSING_JOB_BY_UPLOAD_BATCH_RESET' });
    }
  }, [dispatch, uploadBatchId, currentPage, pageSize, searchText, searchProp]);

  // Auto-refresh effect
  useEffect(() => {
    let interval;

    const statusActive = isUploadBatchAutoRefreshActive(uploadBatchStatus);
    const totalProcessed = statistics?.totalRecordsProcessed || 0;
    const totalRecords = totalUploadBatchRecords;
    const isComplete = totalProcessed >= totalRecords && totalRecords > 0;

    if (autoRefresh && uploadBatchId && statusActive && !isComplete) {
      interval = setInterval(() => {
        const params = {
          page: currentPage,
          limit: pageSize,
          ...(searchText && searchProp && { filter: searchProp, query: searchText }),
        };
        dispatch(getProcessingJobsByUploadBatch(uploadBatchId, params));
        if (typeof onRefreshUploadBatch === 'function') {
          onRefreshUploadBatch(uploadBatchId);
        }
      }, 3000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [
    autoRefresh,
    uploadBatchId,
    uploadBatchStatus,
    currentPage,
    pageSize,
    searchText,
    searchProp,
    dispatch,
    onRefreshUploadBatch,
    statistics?.totalRecordsProcessed,
    totalUploadBatchRecords,
  ]);

  // Reset state on unmount
  useEffect(() => {
    return () => {
      dispatch({ type: 'PROCESSING_JOB_BY_UPLOAD_BATCH_RESET' });
    };
  }, [dispatch]);

  // Calculate progress speed and update stats
  useEffect(() => {
    if (statistics && !loadingByUploadBatch) {
      setPreservedStats(statistics);
      
      // Calculate progress speed based on previous stats
      if (previousStats && lastUpdateTime) {
        const timeElapsed = (Date.now() - lastUpdateTime) / 1000; // in seconds
        const recordsProcessed = statistics.totalRecordsProcessed - previousStats.totalRecordsProcessed;
        
        if (timeElapsed > 0 && recordsProcessed > 0) {
          setProgressSpeed(recordsProcessed / timeElapsed);
        } else {
          setProgressSpeed(0);
        }
      }
      
      setPreviousStats(statistics);
      setLastUpdateTime(Date.now());
    }
    
    if (totalUploadBatchRecords > 0 && !loadingByUploadBatch) {
      setPreservedTotalRecords(totalUploadBatchRecords);
    }
  }, [statistics, totalUploadBatchRecords, loadingByUploadBatch]);

  // Disable auto-refresh when processing is complete or batch reached a terminal status
  useEffect(() => {
    if (!autoRefresh) return;

    const statusActive = isUploadBatchAutoRefreshActive(uploadBatchStatus);
    const totalProcessed = statistics?.totalRecordsProcessed || 0;
    const totalRecords = totalUploadBatchRecords;
    const isComplete = totalProcessed >= totalRecords && totalRecords > 0;

    if (!statusActive || isComplete) {
      setAutoRefresh(false);
    }
  }, [autoRefresh, uploadBatchStatus, statistics?.totalRecordsProcessed, totalUploadBatchRecords]);

  // Reset progress tracking when uploadBatchId changes
  useEffect(() => {
    setProgressSpeed(null);
    setPreviousStats(null);
    setLastUpdateTime(null);
    setAutoRefresh(isUploadBatchAutoRefreshActive(uploadBatchStatus));
  }, [uploadBatchId]);

  // Details drawer: fetch full upload batch so pipelineReports / parity populate (list rows often omit jsonb).
  useEffect(() => {
    if (!drawerVisible || !uploadBatchId || typeof onRefreshUploadBatch !== 'function') return;
    onRefreshUploadBatch(uploadBatchId);
  }, [drawerVisible, uploadBatchId, onRefreshUploadBatch]);

  const handleRefresh = () => {
    if (!uploadBatchId) return;
    const params = {
      page: currentPage,
      limit: pageSize,
      ...(searchText && searchProp && { filter: searchProp, query: searchText }),
    };
    dispatch(getProcessingJobsByUploadBatch(uploadBatchId, params));
    if (typeof onRefreshUploadBatch === 'function') {
      onRefreshUploadBatch(uploadBatchId);
    }
  };

  const handleCopyUploadBatchId = useCallback(() => {
    if (!uploadBatchId) return;
    navigator.clipboard.writeText(String(uploadBatchId));
    setUploadBatchIdCopied(true);
    setTimeout(() => setUploadBatchIdCopied(false), 3000);
  }, [uploadBatchId]);

  const clearFilters = () => {
    setSearchText('');
    setSearchProp('jobName');
    setCurrentPage(1);
    setPageSize(10);
  };

  const handleTableChange = (pagination) => {
    if (pagination.current !== currentPage) {
      setCurrentPage(pagination.current);
    }
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (filterType, value) => {
    // Convert filter values to generic filter/query format
    let filter = null;
    let query = null;
    
    if (value !== 'all') {
      switch (filterType) {
        case 'success':
          filter = 'success';
          query = value; // 'success' or 'failure'
          break;
        default:
          break;
      }
    }
    
    // Update search state with generic filter
    setSearchProp(filter || 'jobName');
    setSearchText(query || '');
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSearchPropChange = (value) => {
    setSearchProp(value);
    setCurrentPage(1); // Reset to first page when changing search property
  };

  const handleReset = () => {
    clearFilters();
  };

  // Search options based on backend response
  const searchOptions = [
    { value: 'jobName', label: t('Job Name') },
    { value: 'jobType', label: t('Job Type') },
    { value: 'processorName', label: t('Processor Name') },
    { value: 'status', label: t('Status') },
    { value: 'recordsProcessed', label: t('Records Processed') },
    { value: 'recordsSuccessful', label: t('Records Successful') },
    { value: 'recordsFailed', label: t('Records Failed') }
  ];

  // Helper function to get status tag
  const getStatusTag = (status) => {
    const statusConfig = {
      'Completed': { color: 'green', icon: <CheckCircleOutlined /> },
      'Running': { color: 'blue', icon: <ClockCircleOutlined /> },
      'Pending': { color: 'orange', icon: <ClockCircleOutlined /> },
      'Failed': { color: 'red', icon: <CloseCircleOutlined /> },
      'Cancelled': { color: 'default', icon: <CloseCircleOutlined /> },
      'SKIPPED': { color: 'cyan', icon: <InfoCircleOutlined /> },
    };

    const config = statusConfig[status] || { color: 'default', icon: <InfoCircleOutlined /> };
    
    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  // Helper function to render processing results - compact version without redundant info
  const renderProcessingResults = (results) => {
    if (!results) return null;

    if (results.phase === 'IngestRow') {
      const isFail =
        results.rowOutcome === 'row_failed' ||
        results.colorCode === 'RED' ||
        results.finalStatus === 'REJECTED' ||
        results.finalStatus === 'FAILED';
      const isDup =
        results.rowOutcome === 'duplicate_skipped' ||
        results.colorCode === 'BLUE' ||
        results.finalStatus === 'SKIPPED';
      const alertType = isFail ? 'error' : isDup ? 'info' : 'success';
      return (
        <div style={{ fontSize: '12px' }}>
          <Alert
            type={alertType}
            showIcon
            message={results.outcomeDetail || results.finalStatusReason || results.rowOutcome || 'Ingest row'}
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {results.rowOutcome && (
                  <span>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{t('Outcome code')}:</Text>{' '}
                    <Text code style={{ fontSize: '11px' }}>{results.rowOutcome}</Text>
                  </span>
                )}
                {results.identifiers && (
                  <span>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{t('Identifiers')}:</Text>{' '}
                    <Text code style={{ fontSize: '11px' }}>
                      {JSON.stringify(results.identifiers)}
                    </Text>
                  </span>
                )}
                {results.compositeKey && (
                  <span>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{t('Composite key')}:</Text>{' '}
                    <Text code style={{ fontSize: '11px' }}>
                      {JSON.stringify(results.compositeKey)}
                    </Text>
                  </span>
                )}
                {results.nhsValidation != null && (
                  <span>
                    <Tag color={results.nhsValidation.valid ? 'green' : 'red'}>
                      NHS: {results.nhsValidation.valid ? t('Valid') : t('Invalid')}
                    </Tag>
                    {results.nhsValidation.reason && (
                      <Text type="secondary" style={{ fontSize: '11px', marginLeft: 8 }}>
                        {results.nhsValidation.reason}
                      </Text>
                    )}
                  </span>
                )}
                {results.dataQuality && (results.dataQuality.status || results.dataQuality.reason) && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{t('Data quality')}:</Text>
                    <span
                      role="img"
                      aria-label={results.dataQuality.status || 'data quality'}
                      title={[results.dataQuality.status, results.dataQuality.reason].filter(Boolean).join(' — ')}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: dataQualityStatusColor(results.dataQuality.status),
                        flexShrink: 0,
                        border: '1px solid rgba(0,0,0,0.12)',
                      }}
                    />
                    {results.dataQuality.reason && (
                      <Text style={{ fontSize: '11px' }}>{results.dataQuality.reason}</Text>
                    )}
                  </span>
                )}
                {results.insertedCosdId && (
                  <span><Text type="secondary">{t('Inserted id')}:</Text> <Text code>{results.insertedCosdId}</Text></span>
                )}
                {results.insertedSactId && (
                  <span><Text type="secondary">{t('Inserted id')}:</Text> <Text code>{results.insertedSactId}</Text></span>
                )}
                {results.insertedRtdsAttendanceId && (
                  <span><Text type="secondary">{t('Inserted id')}:</Text> <Text code>{results.insertedRtdsAttendanceId}</Text></span>
                )}
                {results.insertedRtdsPrescriptionId && (
                  <span><Text type="secondary">{t('Inserted id')}:</Text> <Text code>{results.insertedRtdsPrescriptionId}</Text></span>
                )}
                {results.entityIds && (
                  <span>
                    <Text type="secondary" style={{ fontSize: '11px' }}>{t('Entity ids')}:</Text>{' '}
                    <Text code style={{ fontSize: '10px' }}>{JSON.stringify(results.entityIds)}</Text>
                  </span>
                )}
                {results.errors?.length > 0 && (
                  <span>
                    <Text type="danger" style={{ fontSize: '11px' }}>{t('Errors')}:</Text>{' '}
                    {results.errors.join('; ')}
                  </span>
                )}
                {results.errorMessage && isFail && (
                  <Alert type="error" message={results.errorMessage} style={{ marginTop: 4 }} />
                )}
              </Space>
            }
            style={{ fontSize: '12px' }}
          />
        </div>
      );
    }

    const hasValidationData = results.nhsValidationStatus !== undefined || results.locationInScope !== undefined;
    const hasGeographicData = results.geographicData && (
      results.geographicData.ccgCode || 
      (results.geographicData.latitude && results.geographicData.longitude)
    );

    return (
      <div style={{ fontSize: '12px' }}>
        {/* Status Reason */}
        {results.finalStatusReason && (
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ color: '#666', fontSize: '11px' }}>Reason:</Text>
            <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>{results.finalStatusReason}</div>
          </div>
        )}

        {/* Validation Results */}
        {hasValidationData && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ color: '#666', fontSize: '11px' }}>Validation Results:</Text>
            <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {results.nhsValidationStatus !== undefined && (
                <div>
                  <Tag color={results.nhsValidationStatus ? 'green' : 'red'} size="small">
                    NHS: {results.nhsValidationStatus ? 'Valid' : 'Invalid'}
                  </Tag>
                  {results.nhsValidationReason && (
                    <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>{results.nhsValidationReason}</div>
                  )}
                </div>
              )}
              {results.locationInScope !== undefined && (
                <div>
                  <Tag color={results.locationInScope ? 'green' : 'red'} size="small">
                    Location: {results.locationInScope ? 'In Scope' : 'Out of Scope'}
                  </Tag>
                  {results.normalizedODSCode && (
                    <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>ODS: {results.normalizedODSCode}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Geographic Data */}
        {hasGeographicData && (
          <div style={{ marginBottom: 8 }}>
            <Text strong style={{ color: '#666', fontSize: '11px' }}>Geographic:</Text>
            <div style={{ marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: '11px' }}>
              {results.geographicData.ccgCode && (
                <span>CCG: <Text code>{results.geographicData.ccgCode}</Text></span>
              )}
              {results.geographicData.latitude && results.geographicData.longitude && (
                <span>Coords: <Text code>{results.geographicData.latitude}, {results.geographicData.longitude}</Text></span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };



  // Table columns
  const columns = [
    {
      title: t('Job Name'),
      dataIndex: 'jobName',
      key: 'jobName',
      width: 220,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{text}</div>
          <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
            {record.jobType} • {record.processorName}
          </div>
        </div>
      )
    },
    {
      title: t('NHS Validation'),
      key: 'nhsValidation',
      width: 120,
      render: (_, record) => {
        const results = parseProcessingJobJson(record.processingResults);
        if (!results) return '-';
        if (results.nhsValidation != null) {
          const ok = results.nhsValidation.valid;
          return (
            <Tag color={ok ? 'green' : 'red'} size="small">
              {ok ? t('Valid') : t('Invalid')}
            </Tag>
          );
        }
        if (results.nhsValidationStatus === undefined) return '-';
        return (
          <Tag color={results.nhsValidationStatus ? 'green' : 'red'} size="small">
            {results.nhsValidationStatus ? t('Valid') : t('Invalid')}
          </Tag>
        );
      }
    },
    {
      title: t('Location'),
      key: 'location',
      width: 120,
      render: (_, record) => {
        const results = parseProcessingJobJson(record.processingResults);
        if (!results || results.locationInScope === undefined) return '-';
        return (
          <Tag color={results.locationInScope ? 'green' : 'red'} size="small">
            {results.locationInScope ? t('In Scope') : t('Out of Scope')}
          </Tag>
        );
      }
    },
    {
      title: (
        <Space>
          {t('Final Status')}
          <Popover
            content={
              <div style={{ fontSize: '12px' }}>
                <p><strong>APPROVED:</strong> Record passed all validations and is ready for submission</p>
                <p><strong>PENDING_REVIEW:</strong> Record has validation issues requiring manual review</p>
                <p><strong>REJECTED:</strong> Record failed critical validations and cannot be processed</p>
                <p><strong>PENDING:</strong> Record is awaiting processing</p>
              </div>
            }
            title={t('Final Status Explanation')}
            trigger="hover"
          >
            <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
          </Popover>
        </Space>
      ),
      key: 'finalStatus',
      width: 180,
      render: (_, record) => {
        const pr = parseProcessingJobJson(record.processingResults);
        const finalStatus = pr?.finalStatus || record.status;
        const detail = pr?.outcomeDetail || pr?.finalStatusReason;
        const statusConfig = {
          'APPROVED': { color: 'green', icon: <CheckCircleOutlined /> },
          'REJECTED': { color: 'red', icon: <CloseCircleOutlined /> },
          'PENDING': { color: 'orange', icon: <ClockCircleOutlined /> },
          'PENDING_REVIEW': { color: 'orange', icon: <ClockCircleOutlined /> },
          'Completed': { color: 'green', icon: <CheckCircleOutlined /> },
          'Running': { color: 'blue', icon: <ClockCircleOutlined /> },
          'Failed': { color: 'red', icon: <CloseCircleOutlined /> },
          'SKIPPED': { color: 'cyan', icon: <InfoCircleOutlined /> },
        };

        const config = statusConfig[finalStatus] || { color: 'default', icon: <InfoCircleOutlined /> };

        return (
          <Space direction="vertical" size={2} style={{ maxWidth: 260 }}>
            <Tag color={config.color} icon={config.icon}>
              {finalStatus}
            </Tag>
            {detail && (
              <Text type="secondary" style={{ fontSize: '11px', lineHeight: 1.35 }} ellipsis={{ tooltip: detail }}>
                {detail}
              </Text>
            )}
          </Space>
        );
      }
    },
    {
      title: t('Records'),
      key: 'records',
      width: 120,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div style={{ color: '#52c41a' }}>Success: {record.recordsSuccessful || 0}</div>
          <div style={{ color: '#f5222d' }}>Failed: {record.recordsFailed || 0}</div>
        </div>
      )
    },
    {
      title: t('Duration'),
      key: 'duration',
      width: 100,
      render: (_, record) => {
        if (!record.startTime) return '-';
        
        const start = dayjs(record.startTime);
        const end = record.endTime ? dayjs(record.endTime) : dayjs();
        const duration = end.diff(start, 'millisecond');
        
        if (duration < 1000) {
          return <Text style={{ fontSize: '12px' }}>{duration}ms</Text>;
        } else if (duration < 60000) {
          return <Text style={{ fontSize: '12px' }}>{Math.round(duration / 1000)}s</Text>;
        } else {
          return <Text style={{ fontSize: '12px' }}>{Math.round(duration / 60000)}m</Text>;
        }
      }
    },
    {
      title: t('Timestamps'),
      key: 'timestamps',
      width: 180,
      render: (_, record) => (
        <div style={{ fontSize: '11px' }}>
          {record.startTime && (
            <div><Text strong>Start:</Text> {dayjs(record.startTime).format('DD/MM/YY HH:mm:ss')}</div>
          )}
          {record.endTime && (
            <div><Text strong>End:</Text> {dayjs(record.endTime).format('DD/MM/YY HH:mm:ss')}</div>
          )}
          {record.createdAt && (
            <div><Text strong>Created:</Text> {dayjs(record.createdAt).format('DD/MM/YY HH:mm:ss')}</div>
          )}
        </div>
      )
    }
  ];

  // If no uploadBatchId provided
  if (!uploadBatchId) {
    return (
      <Card title={title} size="small">
        <Alert 
          message={t('No Processing Jobs')} 
          description={t('No upload batch ID provided')} 
          type="info" 
          showIcon 
        />
      </Card>
    );
  }

  // Calculate progress data
  const totalProcessed = statistics?.totalRecordsProcessed || 0;
  const totalSuccessful = statistics?.totalRecordsSuccessful || 0;
  const totalFailed = statistics?.totalRecordsFailed || 0;
  const totalRecords = totalUploadBatchRecords; // Use the value from the endpoint

  // Use preserved stats when loading, otherwise use current stats
  const displayStats = loadingByUploadBatch && preservedStats ? preservedStats : statistics;
  const displayTotalProcessed = displayStats?.totalRecordsProcessed || 0;
  const displayTotalSuccessful = displayStats?.totalRecordsSuccessful || 0;
  const displayTotalFailed = displayStats?.totalRecordsFailed || 0;
  const displayTotalRecords = loadingByUploadBatch && preservedTotalRecords > 0 ? preservedTotalRecords : totalRecords;

  const skippedRecordsFromStats = (() => {
    const dist = displayStats?.statusDistribution;
    if (!dist || typeof dist !== 'object') return null;
    const raw = dist.SKIPPED ?? dist.skipped;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  })();

  const drawerUploadBatchStats =
    uploadBatchFileStats != null
      ? {
          ...uploadBatchFileStats,
          ...(skippedRecordsFromStats != null && { skippedRecords: skippedRecordsFromStats }),
        }
      : null;

  return (
    <>
      <Card
        title={
          <div>
            <Space style={{ marginBottom: 4 }}>
              <Text strong>{title}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ({totalCount || 0} {t('items')})
              </Text>
            </Space>
            {(filePath || fileHash || uploadMethod || customerPrefix || createdAt) && (
              <div style={{ fontSize: '10px', color: '#8c8c8c', marginTop: 4, lineHeight: '1.4' }}>
                <Space size={[8, 4]} wrap>
                  {filePath && (
                    <span style={{ fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <FolderOutlined style={{ fontSize: '10px' }} />
                      <Text type="secondary" style={{ fontSize: '10px' }}>Path:</Text> {filePath}
                    </span>
                  )}
                  {fileHash && (
                    <span style={{ fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <SafetyOutlined style={{ fontSize: '10px' }} />
                      <Text type="secondary" style={{ fontSize: '10px' }}>Hash:</Text> {fileHash}
                    </span>
                  )}
                  {uploadMethod && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <CloudUploadOutlined style={{ fontSize: '10px' }} />
                      {uploadMethod}
                    </span>
                  )}
                  {customerPrefix && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <TagOutlined style={{ fontSize: '10px' }} />
                      {customerPrefix}
                    </span>
                  )}
                  {createdAt && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <CalendarOutlined style={{ fontSize: '10px' }} />
                      {dayjs(createdAt).format('MMM DD, YYYY')}
                    </span>
                  )}
                </Space>
              </div>
            )}
          </div>
        }
        size="small"
        headStyle={{ padding: '12px 16px' }}
        bodyStyle={{ padding: '12px 16px' }}
        extra={
          <Space>
            <Button
              icon={<TableOutlined />}
              onClick={() => setDrawerVisible(true)}
              size="small"
            >
              {t('View Details')}
            </Button>
            <Space size="small">
              <Text style={{ fontSize: '12px' }}>{t('Auto')}:</Text>
              <Switch
                size="small"
                checked={autoRefresh}
                onChange={setAutoRefresh}
                title={autoRefresh ? t('Auto-refresh enabled (3s)') : t('Enable auto-refresh (3s)')}
              />
              
            </Space>
            <Button
              icon={<RefreshIcon />}
              onClick={handleRefresh}
              size="small"
              loading={loadingByUploadBatch}
            >
              {t('Refresh')}
            </Button>
          </Space>
        }
      >
        {/* Statistics Section */}
        <div style={{ marginBottom: 12, padding: '8px', borderRadius: '6px' }}>
            <Row gutter={[12, 6]}>
              <Col span={3}>
                <Statistic
                  title={t('Total Processed')}
                  value={displayTotalRecords > 0 ? ((displayTotalProcessed / displayTotalRecords) * 100).toFixed(2) : '0.00'}
                  suffix="%"
                  valueStyle={{ fontSize: '22px', fontWeight: 'bold' }}
                />
              </Col>
              <Col span={3}>
                <Statistic
                  title={t('Total Jobs')}
                  value={displayStats?.totalJobs ?? 0}
                  valueStyle={{ fontSize: '22px', fontWeight: 'bold' }}
                />
              </Col>
              <Col span={3}>
                <Statistic
                  title={t('Autovalidation Rate')}
                  value={displayTotalProcessed > 0 ? (displayTotalSuccessful / displayTotalProcessed * 100).toFixed(1) : '0.0'}
                  suffix="%"
                  valueStyle={{ fontSize: '22px', fontWeight: 'bold', color: (displayTotalSuccessful / displayTotalProcessed) > 0.5 ? '#52c41a' : '#f5222d' }}
                />
              </Col>
              <Col span={3}>
                <Statistic
                  title={t('Records Processed')}
                  value={displayStats?.totalRecordsProcessed ?? 0}
                  valueStyle={{ fontSize: '22px', fontWeight: 'bold' }}
                />
              </Col>
              <Col span={3}>
                <Statistic
                  title={t('Avg Processing Time')}
                  value={displayStats?.averageProcessingTimeMinutes && displayStats.averageProcessingTimeMinutes > 0 
                    ? (displayStats.averageProcessingTimeMinutes * 60).toFixed(1) 
                    : '-'}
                  suffix={displayStats?.averageProcessingTimeMinutes && displayStats.averageProcessingTimeMinutes > 0 ? 's' : ''}
                  valueStyle={{ fontSize: '22px', fontWeight: 'bold' }}
                />
              </Col>
              <Col span={3}>
                <Statistic
                  title={t('Progress Speed')}
                  value={(() => {
                    const isProcessingComplete = displayTotalProcessed >= displayTotalRecords && displayTotalRecords > 0;
                    if (isProcessingComplete) {
                      return t('Complete');
                    }
                    if (progressSpeed !== null && progressSpeed > 0) {
                      return progressSpeed.toFixed(1);
                    }
                    return '-';
                  })()}
                  suffix={(() => {
                    const isProcessingComplete = displayTotalProcessed >= displayTotalRecords && displayTotalRecords > 0;
                    return isProcessingComplete ? '' : ' rec/s';
                  })()}
                  valueStyle={{ 
                    fontSize: '22px', 
                    fontWeight: 'bold', 
                    color: (() => {
                      const isProcessingComplete = displayTotalProcessed >= displayTotalRecords && displayTotalRecords > 0;
                      if (isProcessingComplete) return '#52c41a';
                      return progressSpeed !== null && progressSpeed > 0 ? '#52c41a' : '#666';
                    })()
                  }}
                />
              </Col>
              <Col span={3}>
                <Statistic
                  title={t('Est. Completion')}
                  value={(() => {
                    // Use progress speed if available, otherwise fall back to average processing time
                    if (progressSpeed && progressSpeed > 0 && displayTotalProcessed < displayTotalRecords) {
                      const remainingRecords = displayTotalRecords - displayTotalProcessed;
                      const estimatedSeconds = remainingRecords / progressSpeed;
                      const hours = Math.floor(estimatedSeconds / 3600);
                      const minutes = Math.floor((estimatedSeconds % 3600) / 60);
                      if (hours > 0) {
                        return `${hours}h ${minutes}m`;
                      } else if (minutes > 0) {
                        return `${minutes}m`;
                      } else {
                        return `${Math.round(estimatedSeconds)}s`;
                      }
                    } else if (displayStats?.averageProcessingTimeMinutes && displayTotalProcessed > 0 && displayTotalProcessed < displayTotalRecords) {
                      const remainingRecords = displayTotalRecords - displayTotalProcessed;
                      const avgTimePerRecord = displayStats.averageProcessingTimeMinutes * 60; // in seconds
                      const estimatedSeconds = (remainingRecords * avgTimePerRecord) / displayStats.totalJobs;
                      const hours = Math.floor(estimatedSeconds / 3600);
                      const minutes = Math.floor((estimatedSeconds % 3600) / 60);
                      if (hours > 0) {
                        return `${hours}h ${minutes}m`;
                      } else if (minutes > 0) {
                        return `${minutes}m`;
                      } else {
                        return `${Math.round(estimatedSeconds)}s`;
                      }
                    }
                    return '-';
                  })()}
                  valueStyle={{ fontSize: '22px', fontWeight: 'bold', color: '#1890ff' }}
                />
              </Col>
            </Row>
            
            {/* Single Progress Bar */}
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text strong style={{ fontSize: '13px' }}>{t('Processing Progress')}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>{totalProcessed} / {displayTotalRecords} {t('records')}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: '11px' }}>{t('Passed System Validation')}: {displayTotalSuccessful}</Text>
                <Text style={{ fontSize: '11px' }}>{t('Failed System Validation')}: {displayTotalFailed}</Text>
              </div>
              <Progress
                percent={displayTotalProcessed > 0 ? (displayTotalProcessed / displayTotalRecords) * 100 : 0}
                strokeColor={(() => {
                  const successRate = displayTotalProcessed > 0 ? (displayTotalSuccessful / displayTotalProcessed) * 100 : 0;
                  
                  // If all records are successful, use solid green
                  if (successRate >= 100) {
                    return '#52c41a';
                  }
                  
                  // If no records are successful, use solid red
                  if (successRate <= 0) {
                    return '#f5222d';
                  }
                  
                  // Otherwise, use gradient from green to red
                  return {
                    '0%': '#52c41a',
                    [`${successRate}%`]: '#52c41a',
                    [`${successRate}%`]: '#f5222d',
                    '100%': '#f5222d',
                  };
                })()}
                showInfo={false}
                size="small"
              />
            </div>
          </div>
      </Card>

      {/* Drawer for detailed table */}
      <Drawer
        title={
          <div>
            <Space>
              <Text strong>{t('Processing Jobs Details')}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ({totalCount || 0} {t('items')})
              </Text>
            </Space>
            {uploadBatchId && (
              <div
                style={{
                  marginTop: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexWrap: 'wrap',
                }}
              >
                <Text type="secondary" style={{ fontSize: '10px' }}>
                  {t('Upload batch ID')}:
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: '10px', fontFamily: 'monospace', margin: 0 }}
                >
                  {uploadBatchId}
                </Text>
                <Button
                  type="text"
                  size="small"
                  icon={uploadBatchIdCopied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyUploadBatchId}
                  style={{
                    padding: '0 4px',
                    height: 'auto',
                    minWidth: 20,
                    color: uploadBatchIdCopied ? token.colorSuccess : token.colorTextTertiary,
                  }}
                  title={uploadBatchIdCopied ? t('Copied!') : t('Copy upload batch ID for audit')}
                />
              </div>
            )}
          </div>
        }
        placement="bottom"
        height="70%"
        onClose={() => {
          clearFilters();
          setUploadBatchIdCopied(false);
          setDrawerVisible(false);
        }}
        open={drawerVisible}
        styles={{ body: { padding: '12px 16px' } }}
        extra={
          <Space>
            <Space size="small">
              <Text style={{ fontSize: '12px' }}>{t('Auto')}:</Text>
              <Switch
                size="small"
                checked={autoRefresh}
                onChange={setAutoRefresh}
                title={autoRefresh ? t('Auto-refresh enabled (3s)') : t('Enable auto-refresh (3s)')}
              />
            </Space>
            <Button
              icon={<RefreshIcon />}
              onClick={handleRefresh}
              size="small"
              loading={loadingByUploadBatch}
            >
              {t('Refresh')}
            </Button>
          </Space>
        }
      >
        <Row gutter={[12, 16]} align="top">
          <Col xs={24} lg={17} style={{ minWidth: 0 }}>
        {/* Search and Filter Controls */}
        <div style={{ marginBottom: 12 }}>
          <Space wrap style={{ marginBottom: 8 }}>
            <span style={{ fontSize: '12px', color: '#666' }}>{t('Filter')}:</span>
            <Select
              placeholder={t("Search by")}
              style={{ width: '140px' }}
              size="small"
              value={searchProp}
              onChange={handleSearchPropChange}
            >
              {searchOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            <Input.Search
              placeholder={`${t("Search by")} ${searchOptions.find(opt => opt.value === searchProp)?.label || ''}`}
              onSearch={handleSearch}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ minWidth: '200px' }}
              size="small"
            />
            <Button
              type="default"
              icon={<ResetIcon />}
              onClick={handleReset}
              size="small"
            >
              {t("Reset")}
            </Button>
          </Space>

          {/* Radio Button Filters - All on one line */}
          <div style={{ marginTop: 6 }}>
            <Space wrap size="small" style={{ width: '100%' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>{t('Success')}:</span>
              <Radio.Group
                value={searchProp === 'success' ? searchText : 'all'}
                onChange={(e) => handleFilterChange('success', e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="all">{t('All')}</Radio.Button>
                <Radio.Button value="success">{t('Success')}</Radio.Button>
                <Radio.Button value="failure">{t('Failure')}</Radio.Button>
              </Radio.Group>
            </Space>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={processingJobs}
          rowKey="id"
          loading={loadingByUploadBatch}
          size="small"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            showQuickJumper: totalCount > 50,
            showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`,
            pageSizeOptions: ['5', '10', '20', '50'],
            size: 'small'
          }}
          onChange={handleTableChange}
          scroll={{ x: 900, y: 380 }}
          bordered
          expandable={{
            expandedRowRender: (record) => (
              <div style={{ padding: '6px 12px' }}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  
                  {/* Job Configuration - Compact */}
                  {record.jobConfiguration && (
                    <div>
                      {(() => {
                        const jc = parseProcessingJobJson(record.jobConfiguration);
                        if (jc?.phase === 'IngestRow') {
                          return (
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '12px' }}>
                              <span>
                                <Text strong style={{ color: '#666', fontSize: '11px' }}>{t('Source')}:</Text>
                                <Text code style={{ marginLeft: 4 }}>{jc.source}</Text>
                              </span>
                              <span>
                                <Text strong style={{ color: '#666', fontSize: '11px' }}>{t('Lane')}:</Text>
                                <Text code style={{ marginLeft: 4 }}>{jc.recordTypeOrLane}</Text>
                              </span>
                              {jc.recordKey && (
                                <span>
                                  <Text strong style={{ color: '#666', fontSize: '11px' }}>{t('Record key')}:</Text>
                                  <Text code style={{ marginLeft: 4 }}>{jc.recordKey}</Text>
                                </span>
                              )}
                              {jc.cohortId && (
                                <span>
                                  <Text strong style={{ color: '#666', fontSize: '11px' }}>{t('Cohort')}:</Text>
                                  <Text code style={{ marginLeft: 4 }}>{jc.cohortId}</Text>
                                </span>
                              )}
                            </div>
                          );
                        }
                        return (
                          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '12px' }}>
                            {jc?.patientId && (
                              <span>
                                <Text strong style={{ color: '#666', fontSize: '11px' }}>Patient:</Text>
                                <Text code style={{ marginLeft: 4 }}>{jc.patientId}</Text>
                              </span>
                            )}
                            {jc?.sourceRecord?.testResultId && (
                              <span>
                                <Text strong style={{ color: '#666', fontSize: '11px' }}>Test ID:</Text>
                                <Text code style={{ marginLeft: 4 }}>{jc.sourceRecord.testResultId}</Text>
                              </span>
                            )}
                            {jc?.sourceRecord?.sourceLocationCode && (
                              <span>
                                <Text strong style={{ color: '#666', fontSize: '11px' }}>Location:</Text>
                                <Text code style={{ marginLeft: 4 }}>{jc.sourceRecord.sourceLocationCode}</Text>
                              </span>
                            )}
                            {jc?.sourceRecord?.postcode && (
                              <span>
                                <Text strong style={{ color: '#666', fontSize: '11px' }}>Postcode:</Text>
                                <Text code style={{ marginLeft: 4 }}>{jc.sourceRecord.postcode}</Text>
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Processing Results - Compact */}
                  {record.processingResults &&
                    renderProcessingResults(parseProcessingJobJson(record.processingResults))}

                  {/* Error Information - Compact */}
                  {(record.errorMessage || record.errorDetails) && (
                    <div style={{ marginTop: 8 }}>
                      <Space direction="vertical" style={{ width: '100%' }} size="small">
                        {record.errorMessage && (
                          <Alert
                            message={record.errorMessage}
                            type="error"
                            size="small"
                            showIcon
                            style={{ fontSize: '11px' }}
                          />
                        )}
                        {record.errorDetails && record.errorDetails !== record.errorMessage && (
                          <Alert
                            message={record.errorDetails}
                            type="warning"
                            size="small"
                            showIcon
                            style={{ fontSize: '11px' }}
                          />
                        )}
                      </Space>
                    </div>
                  )}

                  {/* External System - Compact */}
                  {(record.externalJobId || record.externalSystemUrl) && (
                    <div style={{ marginTop: 8, fontSize: '11px', color: '#666' }}>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        {record.externalJobId && (
                          <span>External ID: <Text code>{record.externalJobId}</Text></span>
                        )}
                        {record.externalSystemUrl && (
                          <span>System URL: <Text code style={{ fontSize: '10px' }}>{record.externalSystemUrl}</Text></span>
                        )}
                      </div>
                    </div>
                  )}

                </Space>
              </div>
            ),
            rowExpandable: (record) => 
              record.jobConfiguration || 
              record.processingResults || 
              record.errorMessage || 
              record.errorDetails || 
              record.externalJobId || 
              record.externalSystemUrl,
          }}
        />
          </Col>

          <Col xs={24} lg={7}>
            <div
              style={{
                position: 'sticky',
                top: 0,
                alignSelf: 'flex-start',
                maxHeight: 'min(72vh, 720px)',
                overflowY: 'auto',
                paddingLeft: 4,
              }}
            >
              {drawerUploadBatchStats != null && (
                <>
                  <Title {...drawerSidebarSectionTitleProps}>
                    {t('Processing summary')}
                  </Title>
                  <UploadBatchSidebarLead stats={drawerUploadBatchStats} token={token} t={t} />
                </>
              )}
            </div>
          </Col>
        </Row>
      </Drawer>
    </>
  );
};

export default ProcessingJobs;
